'use client';

import { TagInput, type TagInputItem, type TagInputProps } from '@ctct/summit-extended';
import { type ComponentProps } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { type InputOpts } from '../../types';

const TagInputWrapper = ({
  field: { name, value, onChange, onBlur: fieldOnBlur },
  onChange: handleOnChange,
  onBlur: handleOnBlur,
  maxTags,
  onMaxTagsReached,
  validateTag,
  ...tagInputProps
}: {
  field: {
    name: string;
    value: string[];
    onChange: (value: string[]) => void;
    onBlur: () => void;
  };
  onChange?: (value: string[]) => void;
  onBlur?: (event: React.FocusEvent<HTMLDivElement>) => void;
  maxTags?: number;
  onMaxTagsReached?: () => void;
  validateTag?: (tag: string) => boolean;
} & Omit<ComponentProps<typeof TagInput>, 'tags' | 'onAddTag' | 'onRemoveTag' | 'onChange'>) => {
  // Convert form value (string[]) to TagInputItem[]
  const tags: TagInputItem[] = (value || []).map((label, index) => ({
    id: String(index + 1),
    label,
    hasError: validateTag ? !validateTag(label) : false,
  }));

  const handleAddTag = (text: string) => {
    const currentTags = value || [];
    if (maxTags !== undefined && currentTags.length >= maxTags) {
      if (onMaxTagsReached) onMaxTagsReached();
      return;
    }
    const newValue = [...currentTags, text];
    onChange(newValue);
    if (handleOnChange) handleOnChange(newValue);
  };

  const handleRemoveTag = (tagId: string) => {
    const newValue = (value || []).filter((_, idx) => tags[idx].id !== tagId);
    onChange(newValue);
    if (handleOnChange) handleOnChange(newValue);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    fieldOnBlur();
    if (handleOnBlur) handleOnBlur(e);
  };

  return (
    <TagInput
      key={name}
      tags={tags}
      onAddTag={handleAddTag}
      onRemoveTag={handleRemoveTag}
      {...tagInputProps}
      onBlur={handleInputBlur}
    />
  );
};

const TagInputInput = ({
  formKey,
  registerOpts,
  onChange: handleOnChange,
  ...tagInputProps
}: InputOpts
  & Omit<TagInputProps, 'tags' | 'onAddTag' | 'onRemoveTag'> & {
    onChange?: (value: string[]) => void;
    onBlur?: (event: React.FocusEvent<HTMLDivElement>) => void;
    maxTags?: number;
    onMaxTagsReached?: () => void;
    validateTag?: (tag: string) => boolean;
  }) => {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name={formKey}
      rules={registerOpts}
      render={({ field }) => (
        <TagInputWrapper onChange={handleOnChange} field={field} {...tagInputProps} />
      )}
    />
  );
};

export { TagInputInput };
